/* A leading glyph for a descriptor field's control, so the edit form reads like the mockups (each
 * `.ctl` there carries a material-symbol icon). Resolved automatically from the field so no
 * descriptor has to spell one out — but a field may override with its own `icon` component.
 *
 * Order: explicit `field.icon` → by field name → by entity (for entity fields) → by type. Returns
 * a rendered element (so callers just drop it into a `prefix`), or null when nothing fits.
 */
import React from "react";
import {
  AlignLeftOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ClusterOutlined,
  CompassOutlined,
  EnvironmentOutlined,
  FieldNumberOutlined,
  FileTextOutlined,
  GlobalOutlined,
  GroupOutlined,
  IdcardOutlined,
  InboxOutlined,
  PhoneOutlined,
  PushpinOutlined,
  ShopOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
} from "@ant-design/icons";

/** By exact field name — the strongest signal, since these names are stable across entities. */
const NAME_ICON = {
  name: FileTextOutlined,
  title: FileTextOutlined,
  code: BarcodeOutlined,
  address: EnvironmentOutlined,
  phone_number: PhoneOutlined,
  minutes_recommended: ClockCircleOutlined,
};

/** By the entity an `entity` field points at — reuses the nav/entity glyph vocabulary. */
const ENTITY_ICON = {
  network: ShopOutlined,
  categories_point: AppstoreOutlined,
  categories: AppstoreOutlined,
  countries: GlobalOutlined,
  county: CompassOutlined,
  counties: CompassOutlined,
  locality: ApartmentOutlined,
  localities: ApartmentOutlined,
  merchandiser: IdcardOutlined,
  supervisor: TeamOutlined,
  created_by: TeamOutlined,
  point: PushpinOutlined,
  points: PushpinOutlined,
  point_groups: GroupOutlined,
  networks: ClusterOutlined,
  tags: TagsOutlined,
  brands: TagOutlined,
  products: InboxOutlined,
  subcategories: ApartmentOutlined,
};

/** Fallback by field type. */
const TYPE_ICON = {
  number: FieldNumberOutlined,
  datetime: CalendarOutlined,
  location: EnvironmentOutlined,
  coordinate: EnvironmentOutlined,
  textarea: AlignLeftOutlined,
};

function iconComponent(field) {
  if (field.icon) return field.icon;
  if (NAME_ICON[field.name]) return NAME_ICON[field.name];
  if (field.type === "entity") return ENTITY_ICON[field.entity] || null;
  return TYPE_ICON[field.type] || null;
}

/** The icon as an element ready for a `prefix`, or null. */
export default function fieldIcon(field) {
  const Icon = iconComponent(field);
  return Icon ? <Icon className="pt-field-icon" /> : null;
}
